/**
 * RouteBuilderPage — `/delivery/routes/new`.
 *
 * Manual two-column route builder for the dispatcher:
 *
 *   Left   header form (scheduledStart + estimatedDurationMinutes)
 *   Middle picker  → GET /v1/mail-items?status=OUT_FOR_DELIVERY, paginated
 *   Right  stop list (in order) with ▲ / ▼ / ✕ controls
 *
 * Save fires `POST /v1/routes` (via `createRoute` in lib/api/delivery.ts) with
 * sequence values renumbered 1..N. Address / contact fields come from the
 * mail item's denormalised `receiver` block — when absent, the route is still
 * accepted with an empty `address: {}` so the dispatcher can edit the stop
 * later.
 */
import { type JSX, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, X } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { createRoute } from '../../lib/api/delivery';
import { listMailItems, type MailItemSummary } from '../../lib/api/mail-items';
import { PageHeader, DeferredNotice } from '../shared/StaffUI';

/**
 * The mail-item summary doesn't carry the receiver block on the list endpoint,
 * but the spec says we should still wire `address: item.receiver ?? {}`. The
 * builder treats every item as an optional bag of receiver fields and falls
 * back gracefully when they are absent.
 */
interface MailItemForBuilder extends MailItemSummary {
  readonly receiver?: {
    readonly name?: string;
    readonly phone?: string;
    readonly city?: string;
    readonly state?: string;
    readonly [key: string]: unknown;
  };
}

function toLocalInputValue(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function todayAt9am(): string {
  const d = new Date();
  d.setHours(9, 0, 0, 0);
  return toLocalInputValue(d);
}

function extractServerMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string } | null;
    if (body && typeof body.message === 'string' && body.message.length > 0) {
      return body.message;
    }
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function RouteBuilderPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [scheduledStart, setScheduledStart] = useState<string>(todayAt9am());
  const [estimatedDurationMinutes, setEstimatedDurationMinutes] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [stops, setStops] = useState<readonly MailItemForBuilder[]>([]);
  const [error, setError] = useState<string | null>(null);

  const itemsQuery = useQuery({
    queryKey: ['delivery', 'route-builder', 'items', { page }],
    queryFn: () => listMailItems({ status: 'OUT_FOR_DELIVERY', page, pageSize: 50 }),
    retry: false,
    staleTime: 30_000,
  });

  const denied =
    itemsQuery.isError &&
    itemsQuery.error instanceof ApiError &&
    (itemsQuery.error.status === 401 || itemsQuery.error.status === 403);

  const selectedIds = useMemo(() => new Set(stops.map((s) => s.id)), [stops]);

  const total = itemsQuery.data?.total ?? 0;
  const pageSize = itemsQuery.data?.pageSize ?? 50;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function addStop(item: MailItemSummary): void {
    setStops((prev) => (prev.some((p) => p.id === item.id) ? prev : [...prev, item as MailItemForBuilder]));
  }

  function removeStop(id: string): void {
    setStops((prev) => prev.filter((p) => p.id !== id));
  }

  function moveUp(index: number): void {
    if (index <= 0) return;
    setStops((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index] as MailItemForBuilder, next[index - 1] as MailItemForBuilder];
      return next;
    });
  }

  function moveDown(index: number): void {
    setStops((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1] as MailItemForBuilder, next[index] as MailItemForBuilder];
      return next;
    });
  }

  const saveMut = useMutation({
    mutationFn: () =>
      createRoute({
        scheduledStart: new Date(scheduledStart).toISOString(),
        ...(estimatedDurationMinutes !== '' && {
          estimatedDurationMinutes: Number(estimatedDurationMinutes),
        }),
        stops: stops.map((item, idx) => {
          const receiver = item.receiver;
          const address: Record<string, unknown> = receiver ? { ...receiver } : {};
          return {
            sequence: idx + 1,
            kind: 'DELIVERY' as const,
            mailItemId: item.id,
            address,
            ...(receiver?.name !== undefined && { contactName: receiver.name }),
            ...(receiver?.phone !== undefined && { contactPhone: receiver.phone }),
          };
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['delivery', 'routes'] });
      navigate('/delivery/routes');
    },
    onError: (err) => setError(extractServerMessage(err, 'Save failed')),
  });

  const canSave = stops.length > 0 && scheduledStart !== '' && !saveMut.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        title="New route"
        description="Manually pick mail items and order the stops, then save as DRAFT."
      />

      {error !== null ? (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left — form */}
        <section className="lg:col-span-3 bg-white border border-border rounded-xl p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">Route details</h2>
          <div>
            <label htmlFor="rb-start" className="text-[10px] font-semibold uppercase text-muted">
              Scheduled start
            </label>
            <input
              id="rb-start"
              type="datetime-local"
              aria-label="Scheduled start"
              value={scheduledStart}
              onChange={(e) => setScheduledStart(e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
          <div>
            <label htmlFor="rb-duration" className="text-[10px] font-semibold uppercase text-muted">
              Estimated duration (minutes)
            </label>
            <input
              id="rb-duration"
              type="number"
              min={1}
              aria-label="Estimated duration in minutes"
              value={estimatedDurationMinutes}
              onChange={(e) => setEstimatedDurationMinutes(e.target.value)}
              placeholder="optional"
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
        </section>

        {/* Middle — picker */}
        <section
          data-testid="route-builder-picker"
          className="lg:col-span-5 bg-white border border-border rounded-xl overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Available mail items</h2>
            <p className="text-[11px] text-muted">{total} OUT_FOR_DELIVERY</p>
          </div>
          {itemsQuery.isPending ? (
            <p className="p-6 text-sm text-muted">Loading…</p>
          ) : denied ? (
            <div className="p-3">
              <DeferredNotice title="Permission required">
                The dev token doesn&apos;t carry <code className="font-mono">Mail/readMailItem</code>.
              </DeferredNotice>
            </div>
          ) : itemsQuery.isError ? (
            <div className="p-3">
              <DeferredNotice title="Service unreachable">
                {(itemsQuery.error as Error).message}
              </DeferredNotice>
            </div>
          ) : (itemsQuery.data?.items.length ?? 0) === 0 ? (
            <p className="p-6 text-sm text-muted text-center">
              No OUT_FOR_DELIVERY items found.
            </p>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="px-3 py-2 w-8"></th>
                    <th className="text-left px-3 py-2">Tracking</th>
                    <th className="text-left px-3 py-2">Receiver</th>
                    <th className="text-left px-3 py-2">City/state</th>
                    <th className="text-left px-3 py-2">Service</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(itemsQuery.data?.items ?? []).map((it) => {
                    const item = it as MailItemForBuilder;
                    const checked = selectedIds.has(item.id);
                    const cityState = [item.receiver?.city, item.receiver?.state]
                      .filter((v) => typeof v === 'string' && (v as string).length > 0)
                      .join(', ');
                    return (
                      <tr key={item.id} className={checked ? 'bg-nipost-green/10' : 'hover:bg-gray-50'}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            aria-label={`Add ${item.trackingNumber} to route`}
                            checked={checked}
                            onChange={() => {
                              if (checked) removeStop(item.id);
                              else addStop(item);
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 text-xs font-mono">{item.trackingNumber}</td>
                        <td className="px-3 py-2 text-xs">{item.receiver?.name ?? '—'}</td>
                        <td className="px-3 py-2 text-xs text-muted">{cityState || '—'}</td>
                        <td className="px-3 py-2 text-xs">{item.serviceClass}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex items-center justify-between px-4 py-3 border-t border-border text-xs text-muted">
                <span>
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>

        {/* Right — stops in order */}
        <section
          data-testid="route-builder-stops"
          className="lg:col-span-4 bg-white border border-border rounded-xl overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-border">
            <h2 className="text-sm font-semibold text-gray-900">Route stops (in order)</h2>
            <p className="text-[11px] text-muted">{stops.length} stop{stops.length === 1 ? '' : 's'}</p>
          </div>
          {stops.length === 0 ? (
            <p className="p-6 text-sm text-muted text-center">
              Pick items from the list to add stops.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-3 py-2 w-8">#</th>
                  <th className="text-left px-3 py-2">Tracking</th>
                  <th className="text-left px-3 py-2">Receiver</th>
                  <th className="px-3 py-2 text-right">Reorder</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stops.map((item, idx) => {
                  const cityState = [item.receiver?.city, item.receiver?.state]
                    .filter((v) => typeof v === 'string' && (v as string).length > 0)
                    .join(', ');
                  return (
                    <tr key={item.id}>
                      <td className="px-3 py-2 text-xs font-mono">{idx + 1}</td>
                      <td className="px-3 py-2 text-xs font-mono">{item.trackingNumber}</td>
                      <td className="px-3 py-2 text-xs">
                        <div>{item.receiver?.name ?? '—'}</div>
                        {cityState ? <div className="text-muted">{cityState}</div> : null}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          aria-label={`Move ${item.trackingNumber} up`}
                          onClick={() => moveUp(idx)}
                          disabled={idx === 0}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Move ${item.trackingNumber} down`}
                          onClick={() => moveDown(idx)}
                          disabled={idx === stops.length - 1}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 ml-1"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Remove ${item.trackingNumber}`}
                          onClick={() => removeStop(item.id)}
                          className="p-1 rounded hover:bg-red-50 text-red-600 ml-1"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => navigate('/delivery/routes')}
          className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSave}
          onClick={() => {
            setError(null);
            saveMut.mutate();
          }}
          className="px-4 py-2 bg-nipost-green text-white rounded-lg text-sm font-semibold disabled:opacity-50"
          data-testid="route-builder-save"
        >
          {saveMut.isPending ? 'Saving…' : 'Save as DRAFT'}
        </button>
      </div>
    </div>
  );
}
